/**
 * ProtectedRoute Component
 * 
 * A wrapper component that protects routes requiring authentication.
 * Redirects unauthenticated users to the login page and allows authenticated users to access protected content.
 * 
 * Features:
 * - Checks if user is authenticated
 * - Shows loading state while checking authentication
 * - Redirects to login if not authenticated
 * 
 * Supports adminOnly prop:
 * - If adminOnly is true, redirects non-admin users to the dashboard
 * - If adminOnly is false, allows all authenticated users to access the route
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, isAdmin, loading, adminLoading } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication and admin status
  if (loading || (adminOnly && user && adminLoading)) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    const redirectPath = encodeURIComponent(`${location.pathname}${location.search}${location.hash}`);
    return <Navigate to={`/login?redirectPath=${redirectPath}`} replace />;
  }

  // Redirect to dashboard if trying to access admin page as non-admin
  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Render protected component if authenticated (and admin if required)
  return children;
};

export default ProtectedRoute;
